import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useAppSelector } from "@/hooks/useAppSelector";
import { Button } from "@heroui/react";
import { useCallback, useMemo } from "react";
import Joyride, {
  CallBackProps,
  EVENTS,
  TooltipRenderProps,
} from "react-joyride";
import { useLocation, useNavigate } from "react-router-dom";
import { setTour } from "../store/user/reducer";
import { api } from "@/api";

function Tooltip({
  backProps,
  index,
  isLastStep,
  primaryProps,
  step,
  tooltipProps,
}: TooltipRenderProps) {
  return (
    <div
      {...tooltipProps}
      className="flex flex-col gap-4 bg-background dark:bg-zinc-900 rounded-xl border-2 border-primary p-4">
      <span className="text-foreground font-medium max-w-64">
        {step.content}
      </span>
      <div className="flex flex-row gap-2">
        {index > 0 && (
          <Button
            color="default"
            variant="flat"
            // @ts-expect-error - react-joyride props type mismatch
            onClick={(e) => backProps.onClick(e)}>
            Назад
          </Button>
        )}
        {!isLastStep && (
          <Button
            color="primary"
            variant="flat"
            // @ts-expect-error - react-joyride props type mismatch
            onClick={(e) => primaryProps.onClick(e)}>
            Вперед
          </Button>
        )}
        {isLastStep && (
          <Button
            color="primary"
            variant="flat"
            // @ts-expect-error - react-joyride props type mismatch
            onClick={(e) => backProps.onClick(e)}>
            Закрыть
          </Button>
        )}
      </div>
    </div>
  );
}

export function Tour() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const { tour } = useAppSelector((state) => state.user);
  const currentUser = useAppSelector((state) => state.user);
  const currentLocation = useAppSelector((state) => state.businessProfile);

  const isInitialTour = location.pathname === "/dashboard/selectProfile";

  const initialSteps = [
    {
      target: "#business-profiles-title",
      content:
        "Ниже отображен список бизнес-профилей, которые привязаны к вашему аккаунту. Вы можете выбрать любой из них, чтобы перейти к работе с ним.",
      data: {},
    },
    {
      target: "#create-business-profile-button",
      content:
        "Если вы хотите добавить новый бизнес-профиль, вы можете воспользоваться кнопкой создания.",
      data: {},
    },
    {
      target: "#business-profiles-title",
      content: "Выберите любой бизнес-профиль для продолжения тура.",
      data: {},
    },
  ];

  const platformSteps = useMemo(() => {
    return [
      {
        target: ".dashboard-welcome",
        content:
          "Добро пожаловать в Elasticspace! Это панель управления для администраторов коворкингов.",
        data: {},
      },
      {
        target: ".dashboard-stats",
        content:
          "Здесь отображается общая статистика по вашему бизнес-профилю: количество офисов и локаций.",
        data: {},
      },
      {
        target: ".quick-actions",
        content:
          "Быстрые действия позволяют быстро перейти к основным функциям платформы.",
        data: {
          next: "/dashboard/locations",
        },
      },
      {
        target: "#locations-title",
        content:
          'В разделе "Локации" вы можете управлять всеми локациями вашего коворкинга.',
        data: {
          previous: "/dashboard",
        },
      },
      {
        target: "#create-location-button",
        content:
          "Здесь вы можете создавать новые локации для вашего коворкинга.",
        data: {
          next: "/dashboard/offices",
        },
      },
      {
        target: "#offices-title",
        content:
          'В разделе "Офисы" вы можете управлять всеми офисами во всех локациях.',
        data: {
          previous: "/dashboard/locations",
        },
      },
      {
        target: "#offices-table",
        content:
          "В таблице отображается информация об офисах: название, тип, площадь, статус и арендатор.",
        data: {
          next: "/dashboard/renters",
        },
      },
      {
        target: "#renters-title",
        content:
          'В разделе "Арендаторы" вы можете управлять всеми арендаторами вашего коворкинга.',
        data: {
          previous: "/dashboard/offices",
        },
      },
      {
        target: "#renters-table",
        content:
          "В таблице отображается информация об арендаторах: название компании, ОГРН, владелец и контакты.",
        data: {
          next: "/dashboard/rentedOffices",
        },
      },
      {
        target: "#agreements-title",
        content:
          'В разделе "Договоры" вы можете управлять всеми договорами аренды.',
        data: {
          previous: "/dashboard/renters",
        },
      },
      {
        target: "#agreements-table",
        content:
          "В таблице отображается информация о договорах: арендатор, офис, даты и статус.",
        data: {
          next: "/dashboard/payments",
        },
      },
      {
        target: "#payments-title",
        content:
          'В разделе "Платежи" вы можете управлять всеми платежами и выставлением счетов.',
        data: {
          previous: "/dashboard/agreements",
        },
      },
      {
        target: "#create-bill-button",
        content: "Здесь вы можете создавать новые счета для арендаторов.",
        data: {
          next: "/dashboard/tickets",
        },
      },
      {
        target: "#tickets-title",
        content:
          'В разделе "Тикеты" вы можете обрабатывать обращения от арендаторов.',
        data: {
          previous: "/dashboard/payments",
        },
      },
      {
        target: "#create-ticket-button",
        content:
          "Здесь вы можете создавать тикеты для решения проблем арендаторов.",
        data: {
          next: "/dashboard/users",
        },
      },
      {
        target: "#users-title",
        content:
          'В разделе "Пользователи" вы можете управлять администраторами системы.',
        data: {
          previous: "/dashboard/tickets",
        },
      },
      {
        target: "#create-user-button",
        content:
          "Здесь вы можете добавлять новых администраторов и настраивать их права доступа.",
        data: {
          next: "/dashboard/settings",
        },
      },
      {
        target: "#settings-title",
        content:
          'В разделе "Настройки" вы можете настраивать параметры системы и вашего профиля.',
        data: {
          previous: "/dashboard/users",
        },
      },
      {
        target: ".settings-sections",
        content:
          "Здесь вы можете настраивать различные параметры системы, включая уведомления, безопасность и интеграции.",
        data: {
          next: "/dashboard",
        },
      },
      {
        target: ".dashboard-welcome",
        content:
          "Поздравляем! Вы успешно прошли тур по платформе Elasticspace. Теперь вы готовы эффективно управлять вашим коворкингом!",
        data: {},
      },
    ];
  }, [currentLocation, currentUser]);

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const {
        action,
        step: {
          data: { next, previous },
        },
        index,
        type,
      } = data;

      const isPreviousAction = action === "prev";
      if (type == EVENTS.STEP_AFTER) {
        if (isPreviousAction && previous) {
          dispatch(setTour({ stepIndex: index - 1 }));
          return navigate(previous);
        } else if (!isPreviousAction && next) {
          dispatch(setTour({ stepIndex: index + 1 }));
          return navigate(next);
        }

        dispatch(setTour({ stepIndex: index + 1 }));
        if (index == platformSteps.length - 1) {
          // Mark tour as completed in user state
          dispatch(setTour({ isTourActive: false }));
          api.auth.updateInfo({
            tour_completed: true,
          });
        }
      }
    },
    [dispatch, platformSteps.length, navigate],
  );

  return (
    <Joyride
      steps={isInitialTour ? initialSteps : platformSteps}
      continuous
      disableOverlay
      run={tour.run && tour.isTourActive}
      stepIndex={tour.stepIndex}
      callback={handleJoyrideCallback}
      tooltipComponent={Tooltip}
      locale={{
        back: "Назад",
        close: "Закрыть",
        last: "Последний шаг",
        next: "Вперед",
        skip: "Пропустить",
        open: "Открыть диалог",
        nextLabelWithProgress: "Дальше (шаг {step} из {steps})",
      }}
      styles={{
        beaconInner: {
          backgroundColor: "#318aef",
        },
        beaconOuter: {
          backgroundColor: "#318aef",
          border: "none",
        },
      }}
    />
  );
}
